export type ChatModeForAnnouncements = 'heritage' | 'astro';

export type SystemAnnouncementAction =
  | { kind: 'enable_listening' }
  | { kind: 'open_filters' }
  | { kind: 'open_share' }
  | { kind: 'open_pranayama' }
  | { kind: 'open_quiz' }
  | { kind: 'open_reading_mode' }
  | { kind: 'open_journey' }
  | { kind: 'open_astro' }
  | { kind: 'enable_citation' }
  | { kind: 'open_global_risk' }
  | { kind: 'open_memory' }
  | { kind: 'external'; href: 'https://rigveda.sarvalok.com' | 'https://chant.sarvalok.com' }
  | { kind: 'prefill_heritage_question'; promptKey: string; defaultPrompt: string };

export type SystemAnnouncement = {
  id: string;
  modes: ChatModeForAnnouncements[];
  minResponseCount: number;
  priority: number;
  titleKey: string;
  defaultTitle: string;
  bodyKey: string;
  defaultBody: string;
  primaryLabelKey: string;
  defaultPrimaryLabel: string;
  primaryAction: SystemAnnouncementAction;
  secondaryLabelKey?: string;
  defaultSecondaryLabel?: string;
  secondaryAction?: SystemAnnouncementAction;
  requiresReadableAnswer?: boolean;
  requiresQuizAvailable?: boolean;
  hideWhenListening?: boolean;
};

export const systemAnnouncements: SystemAnnouncement[] = [
  {
    id: 'listening_library',
    modes: ['heritage'],
    minResponseCount: 2,
    priority: 100,
    titleKey: 'chat.system_announcements.cards.listening_library.title',
    defaultTitle: 'Hear the texts, not just summaries',
    bodyKey: 'chat.system_announcements.cards.listening_library.body',
    defaultBody: 'Listening Mode can search recitations and audio for the Rig Veda, Ramayana, Mahabharata, Puranas, Upanishads, Bhagavad Gita, Sri Rudram, suktas, and mantras.',
    primaryLabelKey: 'chat.system_announcements.actions.turn_on_audio',
    defaultPrimaryLabel: 'Turn on Listening Mode',
    primaryAction: { kind: 'enable_listening' },
    secondaryLabelKey: 'chat.system_announcements.actions.open_rig_veda',
    defaultSecondaryLabel: 'Open Rig Veda',
    secondaryAction: { kind: 'external', href: 'https://rigveda.sarvalok.com' },
    hideWhenListening: true,
  },
  {
    id: 'listening_library_astro',
    modes: ['astro'],
    minResponseCount: 2,
    priority: 95,
    titleKey: 'chat.system_announcements.cards.listening_library_astro.title',
    defaultTitle: 'Audio lives in Heritage chat',
    bodyKey: 'chat.system_announcements.cards.listening_library_astro.body',
    defaultBody: 'Listening Mode works in Heritage chat, where Sarvalok can search recitations and audio for the Vedas, Ramayana, Mahabharata, Upanishads, Gita, suktas, and mantras.',
    primaryLabelKey: 'chat.system_announcements.actions.switch_and_listen',
    defaultPrimaryLabel: 'Switch to Heritage and listen',
    primaryAction: { kind: 'enable_listening' },
    secondaryLabelKey: 'chat.system_announcements.actions.open_rig_veda',
    defaultSecondaryLabel: 'Open Rig Veda',
    secondaryAction: { kind: 'external', href: 'https://rigveda.sarvalok.com' },
  },
  {
    id: 'filters',
    modes: ['heritage'],
    minResponseCount: 2,
    priority: 90,
    titleKey: 'chat.system_announcements.cards.filters.title',
    defaultTitle: 'Narrow answers to specific texts',
    bodyKey: 'chat.system_announcements.cards.filters.body',
    defaultBody: 'Filters let you focus Sarvalok on areas like the Vedas, Ayurveda, Mahabharata, Yoga, Dharma, Jyotisha, and more.',
    primaryLabelKey: 'chat.system_announcements.actions.open_filters',
    defaultPrimaryLabel: 'Open Filters',
    primaryAction: { kind: 'open_filters' },
    hideWhenListening: true,
  },
  {
    id: 'reading_mode',
    modes: ['heritage'],
    minResponseCount: 2,
    priority: 85,
    titleKey: 'chat.system_announcements.cards.reading_mode.title',
    defaultTitle: 'Read the source passage itself',
    bodyKey: 'chat.system_announcements.cards.reading_mode.body',
    defaultBody: 'When an answer has readable sources, the book icon opens the original passage so you can move through it page by page.',
    primaryLabelKey: 'chat.system_announcements.actions.read_sources',
    defaultPrimaryLabel: 'Read Sources',
    primaryAction: { kind: 'open_reading_mode' },
    requiresReadableAnswer: true,
    hideWhenListening: true,
  },
  {
    id: 'citations',
    modes: ['heritage'],
    minResponseCount: 7,
    priority: 82,
    titleKey: 'chat.system_announcements.cards.citations.title',
    defaultTitle: 'Use manuscript-grounded answers',
    bodyKey: 'chat.system_announcements.cards.citations.body',
    defaultBody: 'Citation Mode asks Sarvalok to stay closer to source-grounded answers and show the manuscript trail when sources are available.',
    primaryLabelKey: 'chat.system_announcements.actions.enable_citation',
    defaultPrimaryLabel: 'Turn on Citations',
    primaryAction: { kind: 'enable_citation' },
    hideWhenListening: true,
  },
  {
    id: 'rig_veda_explore',
    modes: ['heritage', 'astro'],
    minResponseCount: 7,
    priority: 78,
    titleKey: 'chat.system_announcements.cards.rig_veda_explore.title',
    defaultTitle: 'Explore the Rig Veda directly',
    bodyKey: 'chat.system_announcements.cards.rig_veda_explore.body',
    defaultBody: 'The Rig Veda explorer is also available from Explore, with a dedicated reading and discovery surface.',
    primaryLabelKey: 'chat.system_announcements.actions.open_rig_veda',
    defaultPrimaryLabel: 'Open Rig Veda',
    primaryAction: { kind: 'external', href: 'https://rigveda.sarvalok.com' },
  },
  {
    id: 'chant_practice',
    modes: ['heritage', 'astro'],
    minResponseCount: 7,
    priority: 76,
    titleKey: 'chat.system_announcements.cards.chant_practice.title',
    defaultTitle: 'Practice with chants and mantras',
    bodyKey: 'chat.system_announcements.cards.chant_practice.body',
    defaultBody: 'The Chant surface is built for recitation and practice when you want to listen, repeat, and refine pronunciation.',
    primaryLabelKey: 'chat.system_announcements.actions.open_chant',
    defaultPrimaryLabel: 'Open Chant',
    primaryAction: { kind: 'external', href: 'https://chant.sarvalok.com' },
  },
  {
    id: 'astro_discovery',
    modes: ['heritage'],
    minResponseCount: 7,
    priority: 80,
    titleKey: 'chat.system_announcements.cards.astro_discovery.title',
    defaultTitle: 'Ask your chart, not just the library',
    bodyKey: 'chat.system_announcements.cards.astro_discovery.body',
    defaultBody: 'Astro Mode can answer personal timing, day quality, muhurta, career, and Jyotisha questions using your saved chart.',
    primaryLabelKey: 'chat.system_announcements.actions.open_astro',
    defaultPrimaryLabel: 'Open Astro Mode',
    primaryAction: { kind: 'open_astro' },
  },
  {
    id: 'day_quality',
    modes: ['heritage'],
    minResponseCount: 7,
    priority: 79,
    titleKey: 'chat.system_announcements.cards.day_quality.title',
    defaultTitle: 'Choosing a date that matters?',
    bodyKey: 'chat.system_announcements.cards.day_quality.body',
    defaultBody: 'Day Quality lives inside Astro Mode and uses your saved chart for timing, muhurta, and daily Jyotisha context.',
    primaryLabelKey: 'chat.system_announcements.actions.open_day_quality',
    defaultPrimaryLabel: 'Open Day Quality',
    primaryAction: { kind: 'open_astro' },
  },
  {
    id: 'general_curiosity',
    modes: ['astro'],
    minResponseCount: 2,
    priority: 80,
    titleKey: 'chat.system_announcements.cards.general_curiosity.title',
    defaultTitle: 'Ask the questions you never knew where to ask',
    bodyKey: 'chat.system_announcements.cards.general_curiosity.body',
    defaultBody: 'You can also ask Heritage questions like why Vishnu has four arms, or what precautions matter before mantra practice.',
    primaryLabelKey: 'chat.system_announcements.actions.ask_in_heritage',
    defaultPrimaryLabel: 'Ask in Heritage',
    primaryAction: {
      kind: 'prefill_heritage_question',
      promptKey: 'chat.system_announcements.prompts.vishnu_arms',
      defaultPrompt: 'Why does Vishnu have four arms?',
    },
  },
  {
    id: 'memory',
    modes: ['heritage', 'astro'],
    minResponseCount: 7,
    priority: 70,
    titleKey: 'chat.system_announcements.cards.memory.title',
    defaultTitle: 'Manage what Sarvalok remembers',
    bodyKey: 'chat.system_announcements.cards.memory.body',
    defaultBody: 'Your memory page lets you review saved preferences, sadhana context, recurring questions, and personal notes.',
    primaryLabelKey: 'chat.system_announcements.actions.open_memory',
    defaultPrimaryLabel: 'Manage Memory',
    primaryAction: { kind: 'open_memory' },
  },
  {
    id: 'global_risk',
    modes: ['heritage', 'astro'],
    minResponseCount: 7,
    priority: 65,
    titleKey: 'chat.system_announcements.cards.global_risk.title',
    defaultTitle: 'Watch world events through Jyotisha',
    bodyKey: 'chat.system_announcements.cards.global_risk.body',
    defaultBody: 'Global Risk Radar looks at world-risk signals through Jyotisha and Koorma Chakra framing.',
    primaryLabelKey: 'chat.system_announcements.actions.open_global_risk',
    defaultPrimaryLabel: 'Open Global Risk Radar',
    primaryAction: { kind: 'open_global_risk' },
  },
  {
    id: 'pranayama',
    modes: ['heritage'],
    minResponseCount: 7,
    priority: 60,
    titleKey: 'chat.system_announcements.cards.pranayama.title',
    defaultTitle: 'Practice, not just read',
    bodyKey: 'chat.system_announcements.cards.pranayama.body',
    defaultBody: 'Pranayama Practice gives you a guided breathing surface when you want to step from study into practice.',
    primaryLabelKey: 'chat.system_announcements.actions.open_pranayama',
    defaultPrimaryLabel: 'Open Pranayama',
    primaryAction: { kind: 'open_pranayama' },
  },
  {
    id: 'indic_quiz',
    modes: ['heritage'],
    minResponseCount: 7,
    priority: 55,
    titleKey: 'chat.system_announcements.cards.indic_quiz.title',
    defaultTitle: 'Test what you know',
    bodyKey: 'chat.system_announcements.cards.indic_quiz.body',
    defaultBody: 'Take a short quiz on the Vedas, Upanishads, Mahabharata, or Ayurveda and see how the texts hold up against your intuition.',
    primaryLabelKey: 'chat.system_announcements.actions.open_quiz',
    defaultPrimaryLabel: 'Start quiz',
    primaryAction: { kind: 'open_quiz' },
    requiresQuizAvailable: true,
    hideWhenListening: true,
  },
  {
    id: 'journey_wisdom',
    modes: ['heritage'],
    minResponseCount: 15,
    priority: 45,
    titleKey: 'chat.system_announcements.cards.journey_wisdom.title',
    defaultTitle: 'Take a guided first journey',
    bodyKey: 'chat.system_announcements.cards.journey_wisdom.body',
    defaultBody: 'Journey Into Wisdom gives you a structured way to begin when you want a calmer path than open-ended chat.',
    primaryLabelKey: 'chat.system_announcements.actions.open_journey',
    defaultPrimaryLabel: 'Start Journey',
    primaryAction: { kind: 'open_journey' },
    hideWhenListening: true,
  },
  {
    id: 'share_invite',
    modes: ['heritage', 'astro'],
    minResponseCount: 15,
    priority: 20,
    titleKey: 'chat.system_announcements.cards.share_invite.title',
    defaultTitle: "Know someone who'd appreciate this?",
    bodyKey: 'chat.system_announcements.cards.share_invite.body',
    defaultBody: 'You can tell a friend about Sarvalok in a casual note without sending your private chat or personal answers.',
    primaryLabelKey: 'chat.system_announcements.actions.share_sarvalok',
    defaultPrimaryLabel: 'Tell a friend',
    primaryAction: { kind: 'open_share' },
  },
];
